import { AI_METRICS_API_BASE } from "config";
import { requestWithCookieAuth } from "api";

const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export interface AdoConfig {
  organization: string;
  project: string;
}

export const fetchAdoConfigs = async (): Promise<AdoConfig[]> => {
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/configs`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to fetch ADO configs");
  const data = (await response.json()) as { configs: AdoConfig[] };
  return data.configs;
};

export interface AdoTeam {
  organization: string;
  project: string;
  team_id: string;
  team_name: string;
}

export const fetchAdoTeams = async (): Promise<AdoTeam[]> => {
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/teams`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to fetch ADO teams");
  const data = (await response.json()) as { teams: AdoTeam[] };
  return data.teams.filter(
    (t) => t.team_name != "Cloud" && t.team_name != "360PI",
  );
};

export interface AdoSprint {
  sprint: string;
  sprint_start: string | null;
  sprint_end: string | null;
}

export const fetchAdoSprints = async (): Promise<AdoSprint[]> => {
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/sprints`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to fetch ADO sprints");
  const data = (await response.json()) as { sprints: AdoSprint[] };
  return data.sprints;
};

export interface AdoKpiCard {
  label: string;
  description: string;
  value: number | null;
  unit: string;
  change_pct: number | null;
  direction: "positive" | "negative" | null;
}

export interface AdoDashboardResult {
  organization: string;
  project: string;
  generated_at: string | null;
  stored_at: string;
  velocity_throughput: {
    throughput: AdoKpiCard;
    commitment_reliability: AdoKpiCard;
    cycle_time: AdoKpiCard;
    completed_work_per_person: AdoKpiCard;
    carry_over_rate: AdoKpiCard;
  };
  quality: {
    bug_density_index: AdoKpiCard;
    defect_escape_rate: AdoKpiCard;
    rework_ratio: AdoKpiCard;
    critical_blocker_bugs: AdoKpiCard;
    failed_scenarios_per_item: AdoKpiCard;
    non_production_defects: AdoKpiCard;
  };
  delivery_progress: {
    feature_delivery_rate: AdoKpiCard;
  };
}

export const fetchAdoDashboard = async (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
  priorPeriod?: { start: Date; end: Date },
  isProjectWideQuality = false,
): Promise<AdoDashboardResult[]> => {
  const params = new URLSearchParams();
  if (teams.length > 0) {
    if (!teams.includes("Cloud")) {
      teams.push("Cloud");
    }
    if (!teams.includes("360PI")) {
      teams.push("360PI");
    }
    params.set("teams", JSON.stringify(teams));
  }
  if (sprints.length > 0) params.set("sprints", JSON.stringify(sprints));
  params.set("start_date", toLocalDateString(startDate));
  params.set("end_date", toLocalDateString(endDate));

  if (priorPeriod) {
    params.set("prior_start_date", toLocalDateString(priorPeriod.start));
    params.set("prior_end_date", toLocalDateString(priorPeriod.end));
  }
  params.set("project_wide_quality", String(isProjectWideQuality));

  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/dashboard?${params.toString()}`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to fetch ADO dashboard");
  return (await response.json()) as AdoDashboardResult[];
};

// --- Chart metric responses ---

export interface AdoSprintSeries {
  labels: string[];
  values: (number | null)[];
  value_field: string | null;
}

export interface AdoMetricResult {
  organization: string;
  project: string;
  team_name: string;
  table_data: Record<string, unknown>[];
  sprint_series?: AdoSprintSeries;
}

export type ThroughputByTeamResult = AdoMetricResult;

export type CycleTimeTrendResult = AdoMetricResult;

export type BugSeveritySplitResult = AdoMetricResult;

export type TestCasesByTeamResult = AdoMetricResult;

export type FeatureDeliveryByTeamResult = AdoMetricResult;

async function fetchChartMetric(
  path: string,
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[],
  extraParams?: Record<string, string>,
): Promise<AdoMetricResult[]> {
  const params = new URLSearchParams();
  if (teams.length > 0) params.set("teams", JSON.stringify(teams));
  if (sprints.length > 0) params.set("sprints", JSON.stringify(sprints));
  params.set("start_date", toLocalDateString(startDate));
  params.set("end_date", toLocalDateString(endDate));
  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      params.set(key, value);
    });
  }
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}${path}?${params.toString()}`,
  )) as Response;
  if (!response.ok) throw new Error(`Failed to fetch ${path}`);
  return (await response.json()) as AdoMetricResult[];
}

export const fetchThroughputByTeam = (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
) =>
  fetchChartMetric(
    "/v2/ado/velocity/throughput-by-team",
    teams,
    startDate,
    endDate,
    sprints,
  );

export const fetchCycleTimeTrend = (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
) =>
  fetchChartMetric(
    "/v2/ado/velocity/cycle-time-trend",
    teams,
    startDate,
    endDate,
    sprints,
  );

export const fetchBugSeveritySplit = (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
  isProjectWideQuality = false,
) =>
  fetchChartMetric(
    "/v2/ado/quality/severity-split",
    teams,
    startDate,
    endDate,
    sprints,
    {
      project_wide_quality: String(isProjectWideQuality),
    },
  );

export const fetchTestCasesByTeam = (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
) =>
  fetchChartMetric(
    "/v2/ado/quality/test-cases-by-team",
    teams,
    startDate,
    endDate,
    sprints,
  );

export const fetchFeatureDeliveryByTeam = (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
) =>
  fetchChartMetric(
    "/v2/ado/delivery/feature-by-team",
    teams,
    startDate,
    endDate,
    sprints,
  );

export interface ITeamDailyCostSummary {
  cost: number;
  tokens: number;
}

export const fetchTeamDailyCost = async (
  teamIds: string[],
  startDate: Date,
  endDate: Date,
): Promise<ITeamDailyCostSummary> => {
  const params = new URLSearchParams();
  params.set("starting_date", toLocalDateString(startDate));
  params.set("ending_date", toLocalDateString(endDate));
  teamIds.forEach((teamId) => params.append("team_ids", teamId));

  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/teams/daily-cost/?${params.toString()}`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to fetch team daily cost");
  return (await response.json()) as ITeamDailyCostSummary;
};

export interface ISprintDeliveryRow {
  sprint: string;
  sprint_start: string | null;
  sprint_end: string | null;
  /** Only set when the rows were requested split by team. */
  team: string | null;
  pbi: number;
  bug: number;
  defect: number;
  throughput: number;
  velocity: number;
}

export interface ISprintCostRow {
  sprint: string;
  sprint_start: string | null;
  sprint_end: string | null;
  /** Only set when the rows were requested split by team. */
  team: string | null;
  cost_usd: number;
  tokens: number;
  days_with_cost: number;
}

export interface ISprintDeliveryCostSummary {
  sprint_count: number;
  total_throughput: number;
  total_velocity: number;
  avg_throughput: number;
  avg_velocity: number;
  total_cost_usd: number;
  total_tokens: number;
}

export interface ISprintDeliveryCost {
  sprints: ISprintDeliveryRow[];
  cost: ISprintCostRow[];
  summary: ISprintDeliveryCostSummary;
}

export const fetchSprintDeliveryCost = async (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
  isSplitByTeam = false,
): Promise<ISprintDeliveryCost> => {
  const params = new URLSearchParams();
  if (teams.length > 0) params.set("teams", JSON.stringify(teams));
  if (sprints.length > 0) params.set("sprints", JSON.stringify(sprints));
  params.set("start_date", toLocalDateString(startDate));
  params.set("end_date", toLocalDateString(endDate));
  if (isSplitByTeam) params.set("group_by_team", "true");

  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/velocity/sprint-delivery-cost?${params.toString()}`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to fetch sprint delivery and cost");
  return (await response.json()) as ISprintDeliveryCost;
};

export type AdoRunStatus =
  "pending" | "running" | "success" | "failure" | "discarded";

export interface IAdoRunRecord {
  id: string;
  type: "ado_combined" | "ado_data" | "ado_features" | "anthropic";
  trigger: "on_demand" | "scheduled";
  triggered_by: { id: string | null; display_name: string | null } | null;
  task_id: string;
  task_args: { start_date: string | null; end_date: string | null };
  status: AdoRunStatus;
  enqueued_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  teams_processed: number | null;
  metrics_written: number | null;
}

export interface IAdoRefreshQueued {
  status: "queued";
  task_id: string;
}

export interface IAdoRefreshTaskStatus {
  task_id: string;
  status: string;
  error: string | null;
}

export interface IAdoRefreshProgress {
  current: number;
  total: number | null;
}

export interface IAdoRefreshStatus {
  last_refresh: IAdoRunRecord | null;
  next_scheduled: string;
  in_progress: boolean;
  active_status: AdoRunStatus | null;
  progress: IAdoRefreshProgress | null;
}

export const triggerAdoRefresh = async (): Promise<IAdoRefreshQueued> => {
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/refresh`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  )) as Response;
  if (response.status === 409) {
    const data = (await response.json()) as { detail: string };
    throw Object.assign(
      new Error(data.detail ?? "A refresh is already in progress"),
      { status: 409 },
    );
  }
  if (!response.ok) throw new Error("Failed to trigger ADO refresh");
  return (await response.json()) as IAdoRefreshQueued;
};

export const getAdoRefreshTaskStatus = async (
  taskId: string,
): Promise<IAdoRefreshTaskStatus> => {
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/refresh/${taskId}`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to get refresh task status");
  return (await response.json()) as IAdoRefreshTaskStatus;
};

export const getAdoRefreshStatus = async (): Promise<IAdoRefreshStatus> => {
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/refresh-status`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to get ADO refresh status");
  return (await response.json()) as IAdoRefreshStatus;
};

export const getAdoRefreshHistory = async (
  limit = 20,
  status?: "running" | "success" | "failure",
): Promise<IAdoRunRecord[]> => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set("status", status);
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/refresh/history?${params.toString()}`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to get ADO refresh history");
  return (await response.json()) as IAdoRunRecord[];
};

export interface ITeamVelocitySummary {
  velocity: number;
  item_count: number;
}
export const fetchTeamVelocity = async (
  teams: string[],
  startDate: Date,
  endDate: Date,
  sprints: string[] = [],
): Promise<ITeamVelocitySummary> => {
  const params = new URLSearchParams();
  params.set("starting_date", toLocalDateString(startDate));
  params.set("ending_date", toLocalDateString(endDate));
  teams.forEach((team) => params.append("team_names", team));
  sprints.forEach((sprint) => params.append("sprints", sprint));
  const response = (await requestWithCookieAuth(
    `${AI_METRICS_API_BASE}/v2/ado/velocity/sum/?${params.toString()}`,
  )) as Response;
  if (!response.ok) throw new Error("Failed to fetch team velocity");
  return (await response.json()) as ITeamVelocitySummary;
};
